import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';
import CartItemNotice from './CartItemNotice';
import SizeEstimateNote from './SizeEstimateNote';

const CartDrawer = () => {
  const { cartItems, isOpen, closeCart, removeItem, updateQuantity, subtotal, availability, hasUnavailableItems } = useCart();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loadingItems, setLoadingItems] = useState({});
  const [quantityInputs, setQuantityInputs] = useState({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset quantity inputs when cart is closed
  useEffect(() => {
    if (!isOpen) {
      setQuantityInputs({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.overlay,
    zIndex: theme.zIndex.modal,
    animation: 'fadeIn 0.3s ease-in-out',
    backdropFilter: 'blur(4px)',
  };

  const drawerStyles = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    maxWidth: isMobile ? '100%' : '450px',
    backgroundColor: theme.colors.background,
    boxShadow: theme.shadows.xl,
    zIndex: theme.zIndex.modal + 1,
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideInRight 0.3s ease-in-out',
  };

  const headerStyles = {
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const titleStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text,
  };

  const closeButtonStyles = {
    background: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    fontSize: theme.typography.fontSize.xl,
    cursor: 'pointer',
    padding: '0',
    borderRadius: theme.borderRadius.round,
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  };

  const contentStyles = {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background,
  };

  const emptyStyles = {
    textAlign: 'center',
    padding: theme.spacing.xxl,
    color: theme.colors.textSecondary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  };

  const itemStyles = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: theme.spacing.md,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.borderLight}`,
    transition: 'all 0.3s ease',
    backgroundColor: theme.colors.surface,
    marginBottom: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.sm,
  };

  const imageStyles = {
    width: isMobile ? '100%' : '80px',
    height: isMobile ? '200px' : '80px',
    objectFit: 'cover',
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.surfaceAlt,
    border: `1px solid ${theme.colors.borderLight}`,
  };

  // ... (rest of the file remains unchanged)


  const footerStyles = {
    padding: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.surface,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
  };

  const handleCheckout = () => {
    if (hasUnavailableItems) return;
    closeCart();
    navigate('/checkout');
  };

  const handleViewCart = () => {
    closeCart();
    navigate('/cart');
  };

  const handleQuantityInputChange = (itemKey, value) => {
    // Allow only numbers
    const numValue = value.replace(/[^0-9]/g, '');
    setQuantityInputs(prev => ({ ...prev, [itemKey]: numValue }));
  };

  const handleUpdateQuantity = async (itemKey) => {
    const newQuantity = parseInt(quantityInputs[itemKey] || '0', 10);
    if (newQuantity < 1) {
      // Reset to current quantity if invalid
      const currentItem = cartItems.find(item => 
        (item.cartId || item.cart_id || item.id || item.product_id) === itemKey
      );
      if (currentItem) {
        setQuantityInputs(prev => ({ ...prev, [itemKey]: currentItem.quantity.toString() }));
      }
      return;
    }
    
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }));
    try {
      await updateQuantity(itemKey, newQuantity);
      // Keep the updated quantity in the input
      setQuantityInputs(prev => ({ ...prev, [itemKey]: newQuantity.toString() }));
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleRemoveItem = async (itemKey) => {
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }));
    try {
      await removeItem(itemKey);
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  return (
    <>
      {/* Overlay */}
      <div style={overlayStyles} onClick={closeCart} />

      {/* Drawer */}
      <div style={drawerStyles}>
        {/* Header */}
        <div style={headerStyles}>
          <h2 style={titleStyles}>
            <span style={{ color: theme.colors.primary, marginRight: theme.spacing.xs }}>Shopping Cart</span>
            <span style={{ fontSize: theme.typography.fontSize.lg, color: theme.colors.textSecondary, fontWeight: theme.typography.fontWeight.normal }}>({cartItems.length})</span>
          </h2>
          <button 
            style={closeButtonStyles} 
            onClick={closeCart}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.surfaceAlt;
              e.currentTarget.style.transform = 'rotate(90deg)';
              e.currentTarget.style.color = theme.colors.primary;
              e.currentTarget.style.borderColor = theme.colors.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'rotate(0deg)';
              e.currentTarget.style.color = theme.colors.text;
              e.currentTarget.style.borderColor = theme.colors.border;
            }}
            aria-label="Close Cart"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={contentStyles}>
          {cartItems.length === 0 ? (
            <div style={emptyStyles}>
              <div style={{
                fontSize: '4rem',
                marginBottom: theme.spacing.lg,
                opacity: 0.5,
              }}>
                🛒
              </div>
              <h3 style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.xl,
                marginBottom: theme.spacing.sm,
                color: theme.colors.text,
              }}>
                Your cart is empty
              </h3>
              <p style={{ marginBottom: theme.spacing.xl }}>
                Looks like you haven't added anything yet.
              </p>
              <button
                onClick={() => {
                  closeCart();
                  navigate('/products');
                }}
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                  backgroundColor: 'transparent',
                  border: `2px solid ${theme.colors.primary}`,
                  color: theme.colors.primary,
                  borderRadius: theme.borderRadius.full,
                  fontWeight: theme.typography.fontWeight.bold,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.primary;
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = theme.colors.primary;
                }}
              >
                Start Shopping
              </button>
            </div>
          ) : (
            cartItems.map((item) => {
              const itemKey = item.cartId || item.cart_id || item.id || item.product_id;
              const imageUrl = item.image_url || item.imageUrl || item.images?.[0] || '';
              const isLoading = loadingItems[itemKey];
              return (
                <div 
                  key={itemKey} 
                  style={{
                    ...itemStyles,
                    position: 'relative',
                    opacity: isLoading ? 0.6 : 1,
                    pointerEvents: isLoading ? 'none' : 'auto',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = theme.shadows.md;
                      e.currentTarget.style.borderColor = theme.colors.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                      e.currentTarget.style.borderColor = theme.colors.borderLight;
                    }
                  }}
                >
                  {/* Loading Overlay */}
                  {isLoading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: theme.borderRadius.lg,
                      zIndex: 10,
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        border: `3px solid ${theme.colors.borderLight}`,
                        borderTop: `3px solid ${theme.colors.primary}`,
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }} />
                    </div>
                  )}
                  
                  {/* Image */}
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={item.name}
                      style={imageStyles}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextSibling;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <div style={{
                      ...imageStyles,
                      backgroundColor: '#E5E5E5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }} />
                  )}

                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing.xs }}>
                      <h4 style={{
                        fontFamily: theme.typography.fontFamily.primary,
                        fontWeight: theme.typography.fontWeight.bold,
                        fontSize: theme.typography.fontSize.base,
                        color: '#000000',
                        lineHeight: 1.2,
                        marginRight: theme.spacing.xs,
                      }}>
                        {item.name}
                      </h4>
                      <button
                        onClick={() => handleRemoveItem(itemKey)}
                        disabled={isLoading}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.colors.textSecondary,
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          padding: '0 4px',
                          lineHeight: 1,
                        }}
                        onMouseEnter={(e) => e.target.style.color = theme.colors.error}
                        onMouseLeave={(e) => e.target.style.color = theme.colors.textSecondary}
                        aria-label="Remove item"
                      >
                        ×
                      </button>
                    </div>

                    {(item.variantName || item.subcategory_name) && (
                      <p style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.primary,
                        marginBottom: theme.spacing.xs,
                        fontStyle: 'italic',
                        fontWeight: theme.typography.fontWeight.medium,
                      }}>
                        {item.variantName || item.subcategory_name}
                      </p>
                    )}

                    <CartItemNotice status={availability[itemKey]} />
                    <SizeEstimateNote approxSqft={availability[itemKey]?.approx_sqft} />

                    {item.selected_components && item.selected_components.length > 0 && (
                      <div style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.textSecondary,
                        marginBottom: theme.spacing.sm,
                        padding: '6px 8px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.sm,
                        borderLeft: `2px solid ${theme.colors.accent}`,
                      }}>
                        <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '4px' }}>
                          + Customizations:
                        </div>
                        {item.selected_components.map((comp, idx) => (
                          <div key={idx} style={{ 
                            paddingLeft: '4px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '2px'
                          }}>
                            <span>• {comp.name}{comp.variation_name ? ` (${comp.variation_name})` : ''}</span>
                            {Number(comp.price) > 0 && (
                              <span style={{ marginLeft: '4px', opacity: 0.8 }}>
                                +${Number(comp.price).toFixed(2)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Price with discount badge */}
                    {(() => {
                      const currentPrice = Number(item.price) || 0;
                      const basePrice = Number(item.base_price_without_discount) || currentPrice;
                      const hasDiscount = Math.abs(currentPrice - basePrice) > 0.01;
                      
                      return (
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          {hasDiscount ? (
                            <>
                              <div style={{ marginBottom: '4px' }}>
                                <span style={{
                                  fontSize: theme.typography.fontSize.xs,
                                  color: theme.colors.textSecondary,
                                }}>
                                  Original: 
                                </span>
                                <span style={{
                                  fontSize: theme.typography.fontSize.sm,
                                  color: theme.colors.textSecondary,
                                  textDecoration: 'line-through',
                                  marginLeft: '4px',
                                }}>
                                  ${basePrice.toFixed(2)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span style={{
                                  fontSize: theme.typography.fontSize.sm,
                                  fontWeight: theme.typography.fontWeight.bold,
                                  color: theme.colors.primary,
                                }}>
                                  ${currentPrice.toFixed(2)} each
                                </span>
                                <div style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  backgroundColor: '#dcfce7',
                                  border: '1px solid #86efac',
                                  borderRadius: theme.borderRadius.sm,
                                  fontSize: '0.65rem',
                                  color: '#16a34a',
                                  fontWeight: 600,
                                }}>
                                  {((basePrice - currentPrice) / basePrice * 100).toFixed(0)}% off
                                </div>
                              </div>
                            </>
                          ) : (
                            <div style={{ marginBottom: '4px' }}>
                              <span style={{
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.bold,
                                color: theme.colors.primary,
                              }}>
                                ${currentPrice.toFixed(2)} each
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: isMobile ? theme.spacing.sm : 0, gap: theme.spacing.sm }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: theme.spacing.xs,
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                          <label style={{
                            fontSize: theme.typography.fontSize.xs,
                            color: theme.colors.textSecondary,
                            fontWeight: theme.typography.fontWeight.medium,
                          }}>
                            Qty:
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={quantityInputs[itemKey] !== undefined ? quantityInputs[itemKey] : item.quantity}
                            onChange={(e) => handleQuantityInputChange(itemKey, e.target.value)}
                            disabled={isLoading}
                            style={{
                              width: '50px',
                              padding: '6px 8px',
                              border: `1px solid ${theme.colors.border}`,
                              borderRadius: theme.borderRadius.md,
                              textAlign: 'center',
                              fontWeight: theme.typography.fontWeight.bold,
                              fontSize: theme.typography.fontSize.sm,
                              backgroundColor: theme.colors.surface,
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateQuantity(itemKey);
                              }
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleUpdateQuantity(itemKey)}
                          disabled={isLoading || !quantityInputs[itemKey]}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            backgroundColor: theme.colors.primary,
                            color: '#fff',
                            cursor: quantityInputs[itemKey] ? 'pointer' : 'not-allowed',
                            fontWeight: theme.typography.fontWeight.bold,
                            fontSize: theme.typography.fontSize.xs,
                            borderRadius: theme.borderRadius.md,
                            transition: 'all 0.2s ease',
                            opacity: quantityInputs[itemKey] ? 1 : 0.5,
                          }}
                          onMouseEnter={(e) => {
                            if (quantityInputs[itemKey]) {
                              e.target.style.backgroundColor = theme.colors.primaryDark;
                              e.target.style.transform = 'scale(1.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = theme.colors.primary;
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          Update
                        </button>
                      </div>

                      <div style={{
                        fontFamily: theme.typography.fontFamily.primary,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.primary,
                        fontSize: theme.typography.fontSize.lg,
                      }}>
                        ${((Number(item.price) || 0) * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div style={footerStyles}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: theme.spacing.lg,
              alignItems: 'flex-end',
            }}>
              <span style={{
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.textSecondary,
              }}>
                Subtotal
              </span>
              <span style={{ 
                fontFamily: theme.typography.fontFamily.primary,
                color: theme.colors.text,
                fontSize: theme.typography.fontSize['2xl'],
                fontWeight: theme.typography.fontWeight.bold,
                lineHeight: 1,
              }}>
                ${subtotal.toFixed(2)}
              </span>
            </div>

            {hasUnavailableItems && (
              <p role="status" style={{
                margin: `0 0 ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm,
                color: '#991B1B',
                fontWeight: theme.typography.fontWeight.semibold,
              }}>
                Some items in your cart can’t be ordered right now. Update or remove them to check out.
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <button
                onClick={handleCheckout}
                disabled={hasUnavailableItems}
                style={{
                  width: '100%',
                  padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.bold,
                  fontFamily: theme.typography.fontFamily.secondary,
                  background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
                  color: '#fff',
                  border: 'none',
                  borderRadius: theme.borderRadius.full,
                  boxShadow: hasUnavailableItems ? 'none' : '0 4px 12px rgba(204, 85, 0, 0.3)',
                  cursor: hasUnavailableItems ? 'not-allowed' : 'pointer',
                  opacity: hasUnavailableItems ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing.sm,
                }}
                onMouseEnter={(e) => {
                  if (hasUnavailableItems) return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(204, 85, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  if (hasUnavailableItems) return;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
                }}
              >
                Checkout Now
              </button>
              <button
                onClick={handleViewCart}
                style={{
                  width: '100%',
                  padding: theme.spacing.md,
                  fontSize: theme.typography.fontSize.base,
                  fontWeight: theme.typography.fontWeight.semibold,
                  fontFamily: theme.typography.fontFamily.secondary,
                  background: 'transparent',
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.full,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.surfaceAlt;
                  e.currentTarget.style.borderColor = theme.colors.text;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                View Full Cart
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default CartDrawer;

